import { useQuery } from "@tanstack/react-query";
import type { AllowanceUnit, BillingCurrency, PlanQuote } from "@talenthop/shared";
import { api } from "../../lib/api.js";

/** GET /org/plans (0094) — the customer's plans catalog + pooled usage. The ONE read every
 *  customer surface uses for "how much is left", so they never disagree. */
export interface UnitUsage {
  total: number;
  used: number;
  remaining: number;
  nextExpiry: string | null;
}

export interface OrgPlan {
  id: string;
  name: string;
  description: string | null;
  currency: BillingCurrency;
  priceMinor: number;
  /** struck-out "was" price (display only) */
  compareAtMinor: number | null;
  jobs: number;
  resumes: number;
  interviews: number;
  isCustom: boolean;
  isFeatured: boolean;
  minQty: number;
  maxQty: number;
  validityMonths: number;
}

export interface OrgPlansResponse {
  currency: BillingCurrency;
  symbol: string;
  canBuy: boolean;
  tax: { name: string; pct: number; inclusive: boolean };
  /** owner-set cost of one human-run interview; null hides the "you save" estimate */
  humanInterviewMinor: number | null;
  plans: OrgPlan[];
  usage: Record<AllowanceUnit, UnitUsage>;
  jobsMetered: boolean;
  /** pre-plans prepaid balance; > 0 means resumes / interviews keep working past the allowance */
  legacyBalance: number;
  purchases: {
    id: string;
    planName: string;
    quantity: number;
    totalMinor: number;
    taxMinor: number;
    discountMinor: number;
    status: string;
    at: string;
  }[];
}

export interface QuoteResponse {
  quote: PlanQuote;
  couponError: string | null;
  couponApplied: string | null;
}

export const ORG_PLANS_KEY = ["org-plans"] as const;

export function useOrgPlans(refetchInterval?: number) {
  return useQuery({
    queryKey: ORG_PLANS_KEY,
    queryFn: () => api.get<OrgPlansResponse>("/org/plans"),
    ...(refetchInterval ? { refetchInterval } : {}),
  });
}

/** The server's own customer-readable message (400/402 bodies carry one), else the fallback. */
export function serverMessage(err: unknown, fallback: string): string {
  const body = (err as { body?: { message?: unknown } } | null)?.body;
  return typeof body?.message === "string" && body.message ? body.message : fallback;
}
